import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Intent, Position } from '@blueprintjs/core';
import moment from 'moment';

import { Breadcrumbs, Tabs } from 'components/navigation';
import { Button } from 'components/buttons';
import { FormDateRangeInput } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import { Collapsable, ShowHide } from 'components/layout';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { PageRestricted } from 'pages/errors/page-restricted';
import { EmployeeCircleStatus } from 'components/status';
import { Tooltip } from 'components/tooltip';
import { UserAccess, UserHasAccess } from 'components/user-access';

import { HelperFunctions } from 'helpers';
import { RulesEngineService } from 'services';
import { SetRulesEngineDriver, SetRulesEngineRange } from 'state/actions';
import buildXlsxWorkbook from 'utils/buildXlsxWorkbook';

import { RulesEngineEmployeeSearch, emptyDriverSearch, getDefaultDateRange, isDateRangeValid } from 'pages/internal-admin/rules-engine';
import { buildHarnessResultSets, buildHarnessSheets } from './harnessResultSets';

const requiredAction = "Internal";
const diffViewerRoute = "/internal-admin/rules-engine/diff-viewer";
const isoDateFormat = "YYYY-MM-DD";
const displayDateFormat = "DD/MM/YYYY";
const downloadErrorMessage = "Could not build the Excel workbook. Please try again.";
const utcNotice = "Times in columns marked (UTC) are UTC and are not converted to UK local time. The infringement description is in UK local time.";
const invalidRangeMessage = "The end date cannot be before the start date.";
const rangeHelperText = "Infringements are reported from the start date onwards. Activity before it is still read so the engine has its context, and the end date only bounds what comes back.";

const firstPage = 1;
const defaultSetPageSize = 100;
const dateInputMonthsInPast = 30;

const defaultArray = [];

// The date input refuses dates before its default floor, so a handed-over window reaching further back
// lowers the floor to its start; otherwise the default stands.
function getEarliestSelectableDate(handedOverRange) {
    if (handedOverRange == null) {
        return null;
    }

    const handedOverStart = moment(handedOverRange.startDate, isoDateFormat);
    if (handedOverStart.isBefore(moment().subtract(dateInputMonthsInPast, 'months'))) {
        return handedOverStart.toDate();
    }

    return null;
}

export function RulesEngineHarness({ breadcrumbs = defaultArray }) {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const driver = useSelector((state) => state.rulesEngine.driver);
    const handedOverRange = useSelector((state) => state.rulesEngine.range);
    const diffViewerReturn = useSelector((state) => state.rulesEngine.diffViewerReturn);

    const [search, setSearch] = useState(emptyDriverSearch);
    // A range handed over by the diff viewer is only a starting point: it is read once and edited here.
    const [dateRange, setDateRange] = useState(() => handedOverRange ?? getDefaultDateRange());
    const [earliestSelectableDate] = useState(() => getEarliestSelectableDate(handedOverRange));
    const [setupExpanded, setSetupExpanded] = useState(true);
    const [running, setRunning] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [result, setResult] = useState(null);
    const [runSummary, setRunSummary] = useState(null);
    const [activeSetIndex, setActiveSetIndex] = useState(0);
    const [setPage, setSetPage] = useState(firstPage);
    const [setPageSize, setSetPageSize] = useState(defaultSetPageSize);

    const canAccessPage = UserHasAccess(requiredAction);
    const rangeValid = isDateRangeValid(dateRange.startDate, dateRange.endDate);

    // Re-running the same driver over the same range can only reproduce the result already on screen,
    // so the button stays shut until the range moves.
    const rangeChangedSinceRun = runSummary == null
        || runSummary.startDate !== dateRange.startDate
        || runSummary.endDate !== dateRange.endDate;

    const canRun = canAccessPage && driver != null && rangeValid && !running && rangeChangedSinceRun;

    const latestSelectableDate = moment().toDate();

    useEffect(() => {
        dispatch({ type: "SITE_FULL_WIDTH" });
    }, []);

    // The hand-over is spent once read, so a later visit from the menu starts on the default range.
    useEffect(() => {
        if (handedOverRange != null) {
            dispatch(SetRulesEngineRange(null));
        }
    }, []);

    const resultSets = buildHarnessResultSets(result);
    const activeSet = resultSets[activeSetIndex];

    function onSearchChange(change) {
        setSearch((current) => ({ ...current, ...change }));
    }

    function onDriverSelect(selectedDriver) {
        dispatch(SetRulesEngineDriver(selectedDriver));
        setSetupExpanded(true);
    }

    // Starting again on a different driver: the run on screen belongs to the driver being dropped, so
    // it goes with them. The search results and the range are deliberately kept - both are almost
    // always the ones wanted next.
    function onChangeDriver(event) {
        event.stopPropagation();

        dispatch(SetRulesEngineDriver(null));
        setResult(null);
        setRunSummary(null);
    }

    // Back to the view the operator left, when they came here from one.
    function onOpenDiffViewer() {
        navigate(`${diffViewerRoute}${diffViewerReturn?.search ?? ""}`);
    }

    function onToggleSetup() {
        setSetupExpanded((expanded) => !expanded);
    }

    function onDateRangeChange(range) {
        setDateRange({
            startDate: range.startDate.dateString,
            endDate: range.endDate.dateString
        });
    }

    function onRun() {
        if (!canRun) {
            return;
        }

        setRunning(true);

        const request = {
            employeeId: driver.employeeId,
            accountId: driver.accountId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            includeWorkingData: true
        };

        RulesEngineService.runHarness(request).then((response) => {
            setResult(response);
            setRunSummary({ driver: driver, startDate: dateRange.startDate, endDate: dateRange.endDate });
            // The results are the point once a run lands, so the setup gives the screen back to them.
            setSetupExpanded(false);
            onSetChange(0);
        }, (error) => {
            NotificationToaster.show(Intent.DANGER, error);
        }).finally(() => {
            setRunning(false);
        });
    }

    function onDownload() {
        setDownloading(true);

        buildXlsxWorkbook(buildHarnessSheets(resultSets)).then((workbook) => {
            HelperFunctions.downloadFile(workbook, buildFileName("xlsx"));
        }, () => {
            NotificationToaster.show(Intent.DANGER, downloadErrorMessage);
        }).finally(() => {
            setDownloading(false);
        });
    }

    // The response as the endpoint returned it, without the screen's display formatting, so a run can
    // be replayed as an engine test fixture.
    function onDownloadJson() {
        const json = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });

        HelperFunctions.downloadFile(json, buildFileName("json"));
    }

    function buildFileName(extension) {
        return `rules-engine-harness-${runSummary.driver.employeeId}-${runSummary.startDate}-to-${runSummary.endDate}.${extension}`;
    }

    function buildTabs() {
        return resultSets.map((resultSet, index) => {
            return {
                title: `${resultSet.title} (${resultSet.rows.length})`,
                selected: index === activeSetIndex
            };
        });
    }

    // A six-month range on a busy driver runs to tens of thousands of activity rows, so only the
    // page being looked at is rendered. The tab counts stay whole-set totals, and the workbook is
    // built from the whole set too - paging is a rendering limit, not a limit on the run.
    function buildTableRows() {
        const firstRow = (setPage - 1) * setPageSize;

        return activeSet.rows
            .slice(firstRow, firstRow + setPageSize)
            .map((row) => activeSet.columns.map((column) => column.value(row)));
    }

    function onSetPagingChange(newPage, newPageSize) {
        setSetPage(newPage);
        setSetPageSize(newPageSize);
    }

    // Each set is paged independently, so moving to one has to start at its first page rather than
    // wherever the last set was being read.
    function onSetChange(newSetIndex) {
        setActiveSetIndex(newSetIndex);
        setSetPage(firstPage);
    }

    // One element per fact because the flex header strips leading whitespace. ShowHide builds this
    // before a driver exists, hence the optional chaining.
    function buildDriverTitle() {
        // A driver can have no reference or card, and a missing fact must take its separator with it
        // rather than render as the text "null".
        const facts = [
            { key: "reference", text: driver?.reference },
            { key: "card", text: driver?.cardNumber },
            { key: "account", text: `${driver?.accountName} - ${driver?.memberNumber}` },
            { key: "location", text: driver?.locationName }
        ].filter((fact) => fact.text != null && fact.text !== "");

        return (
            <span className="inline-items" id="rules-engine-selected-driver">
                <EmployeeCircleStatus
                    status={driver?.statusName}
                    tooltip={driver?.statusTooltip}
                    isAgency={driver?.isAgency}
                />
                <span className="break-word">{driver?.name}</span>
                {facts.map((fact) => (<span className="break-word" key={fact.key}>{`· ${fact.text}`}</span>))}
            </span>
        );
    }

    // Minimal and icon-only like every other collapsable header button: a filled button on the dark
    // header hides its own icon.
    function buildSetupButtons() {
        return (
            <Tooltip content="Change driver" position={Position.TOP}>
                <Button
                    id="rules-engine-change-driver"
                    text="Change driver"
                    icon="refresh"
                    iconOnly
                    minimal
                    large={false}
                    disabled={running}
                    onClick={onChangeDriver}
                />
            </Tooltip>
        );
    }

    let rangeText = "";
    let rangeError = "";

    // The header labels the results on screen, so it names the range that was run - not the one
    // being edited, and nothing before a run exists.
    if (runSummary != null) {
        const rangeStart = moment(runSummary.startDate, isoDateFormat).format(displayDateFormat);
        const rangeEnd = moment(runSummary.endDate, isoDateFormat).format(displayDateFormat);

        rangeText = `${rangeStart} to ${rangeEnd}`;
    }

    if (dateRange.startDate != null && dateRange.endDate != null && !rangeValid) {
        rangeError = invalidRangeMessage;
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="row">
                        <Breadcrumbs items={breadcrumbs} />
                        <h1>Rules Engine Test Harness</h1>

                        <div className="spacer-bottom-medium">
                            <Button
                                id="rules-engine-open-diff-viewer"
                                text="Diff viewer"
                                icon="comparison"
                                onClick={onOpenDiffViewer}
                            />
                        </div>

                        <ShowHide
                            evaluator={result == null}
                            show={(
                                <div className="intro-text">
                                    <p>Run the rules engine for a driver and date range and inspect every working set it produced. Nothing is saved - a run does not write infringements and does not affect the shadow comparison.</p>
                                </div>
                            )}
                        />

                        <ShowHide
                            evaluator={driver == null}
                            show={(
                                <RulesEngineEmployeeSearch
                                    search={search}
                                    disabled={running}
                                    onSearchChange={onSearchChange}
                                    onDriverSelect={onDriverSelect}
                                />
                            )}
                            hide={(
                                <div className="spacer-bottom">
                                    <Collapsable
                                        title={buildDriverTitle()}
                                        metadata={rangeText}
                                        headerButtons={buildSetupButtons()}
                                        fullWidth
                                        expanded={setupExpanded}
                                        onClick={onToggleSetup}
                                    >
                                        <div className="collapsable-content">
                                            <NotificationInline
                                                intent="warning"
                                                show
                                                allowClose={false}
                                                text={utcNotice}
                                            />

                                            {/* A run needs both dates, so there is nothing for clearing
                                                them to lead to. */}
                                            <FormDateRangeInput
                                                id="rules-engine-analysis-range"
                                                headingText="Analysis range"
                                                helperText={rangeHelperText}
                                                startDate={dateRange.startDate}
                                                endDate={dateRange.endDate}
                                                minDate={earliestSelectableDate}
                                                maxDate={latestSelectableDate}
                                                disabled={running}
                                                allowClear={false}
                                                dangerHelperText={rangeError}
                                                onChange={onDateRangeChange}
                                            />

                                            <Button
                                                id="rules-engine-run"
                                                text={result == null ? "Run" : "Rerun"}
                                                icon="play"
                                                intent="primary"
                                                loading={running}
                                                disabled={!canRun}
                                                onClick={onRun}
                                            />
                                        </div>
                                    </Collapsable>
                                </div>
                            )}
                        />
                    </div>

                    <ShowHide
                        evaluator={result != null}
                        show={(
                            <div className="row">
                                <div className="spacer-bottom-medium inline-items">
                                    <Button
                                        id="rules-engine-download"
                                        text="Download to Excel"
                                        icon="download"
                                        loading={downloading}
                                        disabled={downloading}
                                        onClick={onDownload}
                                    />
                                    <Button
                                        id="rules-engine-download-json"
                                        text="Download JSON"
                                        icon="code"
                                        onClick={onDownloadJson}
                                    />
                                </div>

                                <Tabs
                                    tabs={buildTabs()}
                                    onClick={onSetChange}
                                />

                                <p className="description-text spacer-bottom-small" id="rules-engine-set-description">{activeSet.description}</p>

                                <div className="spacer-bottom wide-table">
                                    <ListingTable
                                        id="listing-table-rules-engine-harness"
                                        headers={activeSet.columns.map((column) => column.header)}
                                        columnWidths={activeSet.columns.map((column) => column.width ?? null)}
                                        data={buildTableRows()}
                                        totalRecordCount={activeSet.rows.length}
                                        loadingData={false}
                                        startPage={setPage}
                                        pageSize={setPageSize}
                                        onPagingChange={onSetPagingChange}
                                        noDataHeading={`No ${activeSet.title.toLowerCase()}`}
                                        noDataMessage="The engine produced no rows for this set over the range you ran."
                                    />
                                </div>
                            </div>
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

RulesEngineHarness.propTypes = {
    breadcrumbs: PropTypes.array
};
